import {
  Languages,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Settings2,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode, type RefObject } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { useSession } from '../../workspace/session.js';
import { workspaceCopy } from '../../merchant/copy.js';
import { ATTENTION_STATUSES } from '../../merchant/model.js';
import {
  MERCHANT_WORKSPACE_NAVIGATION,
  type MerchantWorkspaceNavigationItem,
} from './navigation.js';
import { ChevronIcon, Menu, PlusIcon, useToast } from '../../merchant/ui/primitives.js';
import {
  useMerchantWorkspaceIdentity,
  useWorkspace,
} from '../../merchant/shell-context.js';

function NavigationIcon({ icon: Icon }: { readonly icon: LucideIcon }): ReactNode {
  return <Icon aria-hidden="true" strokeWidth={1.8} />;
}

/**
 * The fixed workspace sidebar from the workspace wireframe: the mark, the
 * workspace switcher, two navigation groups with their badges, and — at the
 * foot — the account, language, sign-out and collapse controls. Under
 * 1024px the same element becomes the drawer; nothing is duplicated into a
 * second navigation.
 */
export function MerchantSidebar({
  collapsed,
  mobileOpen,
  onCollapsedChange,
  onMobileClose,
  sidebarRef,
}: {
  readonly collapsed: boolean;
  readonly mobileOpen: boolean;
  readonly onCollapsedChange: (collapsed: boolean) => void;
  readonly onMobileClose: () => void;
  readonly sidebarRef: RefObject<HTMLElement | null>;
}): ReactNode {
  const { language, toggle } = useLanguage();
  const copy = workspaceCopy(language);
  const { session, logout } = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { businessName } = useMerchantWorkspaceIdentity();
  const workspace = useWorkspace();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const isArabic = language === 'ar';
  const displayName =
    session.status === 'authenticated' ? session.account.displayName : '';
  const accountInitial = displayName
    .trim()
    .slice(0, 1)
    .toLocaleUpperCase(isArabic ? 'ar-SA' : 'en-US');
  const workspaceName = businessName?.trim() || copy.nav.yourBusiness;
  const workspaceInitial = workspaceName
    .slice(0, 1)
    .toLocaleUpperCase(isArabic ? 'ar-SA' : 'en-US');
  const workspaceDomain = (() => {
    const website = workspace.profile?.business.sources.find(
      (source) => source.platform === 'WEBSITE',
    );
    if (website === undefined) return '';
    try {
      return new URL(website.canonicalUrl).hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  })();

  const attentionCount = workspace.lines.filter((line) =>
    ATTENTION_STATUSES.has(line.status),
  ).length;
  const profilePayload = workspace.profile?.profile?.payload;
  const profileIncomplete =
    workspace.profile !== null &&
    (profilePayload === undefined || profilePayload.offers.length === 0);

  const signOut = () => {
    void logout().finally(() => {
      void navigate('/', { replace: true });
    });
  };

  const collapseLabel = collapsed ? copy.nav.expand : copy.nav.collapse;
  const lineCount = workspace.lines.length;

  const badgeFor = (item: MerchantWorkspaceNavigationItem): ReactNode => {
    if (item.destination === 'productionLines' && attentionCount > 0) {
      return <span className="mj-ws-badge">{attentionCount}</span>;
    }
    if (item.destination === 'businessProfile' && profileIncomplete) {
      return <span className="mj-ws-badge is-warn">!</span>;
    }
    return null;
  };

  const ariaFor = (item: MerchantWorkspaceNavigationItem, label: string): string => {
    if (item.destination === 'productionLines' && attentionCount > 0) {
      return `${label}, ${String(attentionCount)} ${copy.nav.attention}`;
    }
    if (item.destination === 'businessProfile' && profileIncomplete) {
      return `${label}, ${copy.nav.incomplete}`;
    }
    return label;
  };

  const renderItem = (item: MerchantWorkspaceNavigationItem) => {
    const label = item.label[language];
    const active = item.matches(pathname);
    // A plain Link: the active state is the route table's `matches`, not
    // NavLink's prefix match, so a tab route still lights its section.
    return (
      <Link
        key={item.destination}
        to={item.path}
        className={`mj-merchant-sidebar-link${active ? ' is-active' : ''}`}
        aria-current={active ? 'page' : undefined}
        aria-label={ariaFor(item, label)}
        title={label}
        onClick={onMobileClose}
      >
        <NavigationIcon icon={item.icon} />
        <span className="mj-merchant-sidebar-label">{label}</span>
        {badgeFor(item)}
      </Link>
    );
  };

  const primary = MERCHANT_WORKSPACE_NAVIGATION.filter(
    (item) => item.group === 'primary',
  );
  const utility = MERCHANT_WORKSPACE_NAVIGATION.filter(
    (item) => item.group === 'utility',
  );

  return (
    <>
      <button
        type="button"
        className={`mj-merchant-sidebar-backdrop${mobileOpen ? ' is-visible' : ''}`}
        aria-label={copy.nav.closeMenu}
        tabIndex={mobileOpen ? 0 : -1}
        onClick={onMobileClose}
      />
      <aside
        id="merchant-workspace-sidebar"
        ref={sidebarRef}
        className={`mj-merchant-sidebar${collapsed ? ' is-collapsed' : ''}${mobileOpen ? ' is-mobile-open' : ''}`}
        aria-label={copy.nav.workspaceNavigation}
        aria-modal={mobileOpen ? true : undefined}
        role={mobileOpen ? 'dialog' : undefined}
      >
        <div className="mj-merchant-sidebar-head">
          <NavLink
            to="/home"
            className="mj-merchant-sidebar-brand"
            aria-label={isArabic ? 'موجة — الرئيسية' : 'Mouja — Home'}
            onClick={onMobileClose}
          >
            <img
              src={
                isArabic
                  ? '/mouja-arabic-pixel-media-logo.svg'
                  : '/mouja-pixel-media-logo.svg'
              }
              alt=""
            />
          </NavLink>
          <button
            type="button"
            className="mj-merchant-icon-button mj-merchant-sidebar-close"
            aria-label={copy.nav.closeMenu}
            onClick={onMobileClose}
          >
            <X aria-hidden="true" strokeWidth={1.8} />
          </button>
        </div>

        {/* The workspace switcher. One business per account today, so the
            menu lists it and states plainly that more are coming. */}
        <div className="mj-merchant-switcher" data-switcher="1">
          <button
            type="button"
            className="mj-merchant-identity"
            aria-haspopup="menu"
            aria-expanded={switcherOpen}
            aria-label={copy.nav.switchWorkspace(workspaceName)}
            title={workspaceName}
            onClick={() => {
              setSwitcherOpen((open) => !open);
            }}
          >
            <span className="mj-merchant-identity-mark" aria-hidden="true">
              {workspaceInitial}
            </span>
            <span className="mj-merchant-identity-copy">
              <span>{workspaceName}</span>
              <small>{workspaceDomain || copy.nav.workspace}</small>
            </span>
            <span className="mj-merchant-identity-chevron">
              <ChevronIcon />
            </span>
          </button>
          <Menu
            open={switcherOpen}
            onClose={() => {
              setSwitcherOpen(false);
            }}
            label={copy.nav.workspaces}
            header={
              <div className="mj-ws-eyebrow mj-ws-menu-eyebrow">
                {copy.nav.workspaces}
              </div>
            }
            items={[
              {
                key: 'current',
                checked: true,
                icon: (
                  <span className="mj-ws-wsav is-small" aria-hidden="true">
                    {workspaceInitial}
                  </span>
                ),
                label: workspaceName,
                trailing: (
                  <span className="mj-ws-muted mj-ws-xs">{copy.nav.current}</span>
                ),
                onSelect: () => {
                  setSwitcherOpen(false);
                },
              },
            ]}
            footer={
              <>
                <div className="mj-ws-menu-sep" />
                <button
                  type="button"
                  role="menuitem"
                  className="mj-ws-mitem"
                  aria-describedby="mj-ws-switcher-soon"
                  onClick={() => {
                    setSwitcherOpen(false);
                    toast(copy.workspaceModal.soon, 6000);
                  }}
                >
                  <PlusIcon />
                  <span className="mj-ws-mitem-label">{copy.nav.createWorkspace}</span>
                  <span className="mj-ws-soon" id="mj-ws-switcher-soon">
                    {copy.common.soon}
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="mj-ws-mitem"
                  onClick={() => {
                    setSwitcherOpen(false);
                    onMobileClose();
                    void navigate('/settings/workspaces');
                  }}
                >
                  <Settings2 aria-hidden="true" strokeWidth={1.8} />
                  <span className="mj-ws-mitem-label">{copy.nav.manageWorkspaces}</span>
                </button>
              </>
            }
          />
        </div>

        <nav className="mj-merchant-sidebar-nav" aria-label={copy.nav.primary}>
          {primary.map(renderItem)}
          <div className="mj-merchant-sidebar-sep" role="presentation" />
          {utility.map(renderItem)}
        </nav>

        <div className="mj-merchant-sidebar-foot">
          {displayName ? (
            <NavLink
              to="/settings/account"
              className="mj-merchant-sidebar-account"
              aria-label={`${copy.nav.account}: ${displayName}`}
              title={displayName}
              onClick={onMobileClose}
            >
              <span className="mj-merchant-sidebar-account-mark" aria-hidden="true">
                {accountInitial || <UserRound strokeWidth={1.8} />}
              </span>
              <span className="mj-merchant-sidebar-account-copy mj-merchant-sidebar-label">
                <span>{displayName}</span>
                <small>{copy.nav.account}</small>
              </span>
            </NavLink>
          ) : null}
          <button
            type="button"
            className="mj-merchant-sidebar-link"
            lang={isArabic ? 'en' : 'ar'}
            title={copy.nav.languageAria}
            onClick={toggle}
          >
            <Languages aria-hidden="true" strokeWidth={1.8} />
            <span className="mj-merchant-sidebar-label">{copy.nav.language}</span>
          </button>
          <button
            type="button"
            className="mj-merchant-sidebar-link"
            aria-label={copy.nav.signOut}
            title={copy.nav.signOut}
            onClick={signOut}
          >
            <LogOut aria-hidden="true" strokeWidth={1.8} />
            <span className="mj-merchant-sidebar-label">{copy.nav.signOut}</span>
          </button>
          <button
            type="button"
            className="mj-merchant-sidebar-link mj-merchant-sidebar-collapse"
            aria-label={collapseLabel}
            aria-expanded={!collapsed}
            title={collapseLabel}
            onClick={() => {
              onCollapsedChange(!collapsed);
            }}
          >
            {collapsed ? (
              <PanelLeftOpen aria-hidden="true" strokeWidth={1.8} />
            ) : (
              <PanelLeftClose aria-hidden="true" strokeWidth={1.8} />
            )}
            <span className="mj-merchant-sidebar-label">
              {collapsed ? copy.nav.expand : copy.nav.collapse}
            </span>
          </button>
          <span className="mj-ws-vh" aria-live="polite">
            {lineCount > 0 ? copy.common.linesCount(lineCount) : ''}
          </span>
        </div>
      </aside>
    </>
  );
}
